import { useCallback, useEffect, useRef, useState } from "react";

type ReplyStatus = "idle" | "sending" | "sent" | "error";

/**
 * Result shape the host's submit callback returns. `success: false`
 * with no `error` falls through to the caller-provided fallback
 * label (`failedToSend` in the i18n bag).
 */
export interface ReplyResult {
  success: boolean;
  error?: string;
}

interface UseReplyStateArgs {
  /**
   * The host-supplied submit callback. The hook owns the text +
   * status state machine and calls this when the user clicks Send;
   * the host wires it to whatever transport applies — extension
   * routes via `browser.runtime.sendMessage`; web routes via
   * `fetch`. Receiving a Promise here keeps the hook agnostic of
   * either while still letting it report sent/error inline.
   *
   * The hook internally guards against re-entrancy (status==="sending")
   * and empty/whitespace-only text, so the callback can assume a
   * trimmed non-empty string and exclusive in-flight access.
   */
  submit: (text: string) => Promise<ReplyResult>;
  /**
   * `true` when the host has the data needed to actually reply
   * (i.e. the notification still has a reply action). When false,
   * `send` is a no-op and `canSend` stays false even with non-empty
   * text. Mirrors the original "is replyAction non-null" check.
   */
  canReply: boolean;
  /**
   * Fallback error string used when the submit callback rejects or
   * returns `success: false` without a specific error message.
   * Caller-provided so the hook stays free of hard-coded English.
   */
  fallbackErrorLabel: string;
}

export interface ReplyState {
  text: string;
  setText: (text: string) => void;
  send: () => void;
  status: ReplyStatus;
  error: string | null;
  canSend: boolean;
}

/**
 * Reply state machine for a single notification. Owns the controlled
 * input value, the sending flag, the sent / error banner, and the
 * auto-clear timer — all the bits that would otherwise fatten the
 * consumer component with bookkeeping. The actual transport is
 * injected via `submit` so the hook works equally for the extension
 * (browser.runtime.sendMessage) and the web (fetch).
 *
 * `send` is stable across renders because every value it reads is
 * mirrored into a ref (`stateRef`). The `useCallback` keyed on
 * `[scheduleClear]` (itself stable) produces a `sendAsync` identity
 * that doesn't change when the surrounding `notification` object is
 * freshly deserialized from storage. Without the ref mirror, the
 * `submit` callback dep would thrash the callback on every storage-
 * driven re-render.
 *
 * Auto-clear: sent → idle after 2s, error → idle after 3s. Pending
 * timers are cancelled on unmount and before any overwrite.
 */
export function useReplyState({
  submit,
  canReply,
  fallbackErrorLabel,
}: UseReplyStateArgs): ReplyState {
  const [text, setText] = useState("");
  const [status, setStatus] = useState<ReplyStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mirror every value `sendAsync` needs so the callback can stay
  // keyed on the stable `scheduleClear` alone. `submit` is a function
  // prop whose identity may change as the parent re-renders; storing
  // the latest reference here lets us call it without re-creating
  // `sendAsync` per render. The mirror lives in useEffect (not the
  // render body) so it only runs for committed renders — React 18
  // concurrent-safe (see #423).
  const stateRef = useRef({ text, status, submit, canReply, fallbackErrorLabel });
  useEffect(() => {
    stateRef.current = { text, status, submit, canReply, fallbackErrorLabel };
  });

  useEffect(
    () => () => {
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    },
    []
  );

  const scheduleClear = useCallback((delayMs: number) => {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    clearTimerRef.current = setTimeout(() => {
      setStatus("idle");
      setError(null);
    }, delayMs);
  }, []);

  const sendAsync = useCallback(async () => {
    const {
      text: current,
      status: currentStatus,
      submit: currentSubmit,
      canReply: currentCanReply,
      fallbackErrorLabel: currentFallback,
    } = stateRef.current;
    if (!current.trim() || !currentCanReply || currentStatus === "sending") return;
    const submitted = current.trim();
    setError(null);
    setStatus("sending");
    try {
      const result = await currentSubmit(submitted);
      if (result.success) {
        setText("");
        setStatus("sent");
        scheduleClear(2000);
      } else {
        setError(result.error ?? currentFallback);
        setStatus("error");
        scheduleClear(3000);
      }
    } catch {
      setError(currentFallback);
      setStatus("error");
      scheduleClear(3000);
    }
  }, [scheduleClear]);

  // Stable void wrapper — keeps `send` identity stable across renders
  // so memoized descendants can consume it as a React event handler
  // without re-rendering on every parent cycle.
  const send = useCallback(() => {
    void sendAsync();
  }, [sendAsync]);

  const canSend = text.trim().length > 0 && status !== "sending" && canReply;

  return { text, setText, send, status, error, canSend };
}
